import { Injectable } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { AuthenticationResult, PopupRequest } from '@azure/msal-browser';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class MicrosoftAuthService {
  private apiUrl = environment.apiUrl;

  constructor(
    private msalService: MsalService,
    private http: HttpClient
  ) {}

  /**
   * Initiates Microsoft login using popup
   */
  loginWithMicrosoft(loginHint?: string): Observable<any> {
    return new Observable(observer => {
      const loginRequest: PopupRequest = {
        scopes: ['user.read', 'openid', 'profile', 'email'],
        // Without this, MSAL's popup silently continues with whichever
        // Microsoft account already has an active SSO session in the browser,
        // ignoring the email the user typed into our own login form. Passing
        // it targets that specific account - prompting for its credentials
        // (or an account picker) instead when it differs from the active session.
        ...(loginHint ? { loginHint } : {}),
      };

      // Ensure MSAL instance is initialized before proceeding
      this.msalService.instance.initialize().then(() => {
        this.msalService.loginPopup(loginRequest)
          .subscribe({
            next: (result: AuthenticationResult) => {
              // The backend validates the ID token and reads the user's identity from it
              this.exchangeTokenWithBackend(result.idToken)
                .subscribe({
                  next: (backendResponse) => {
                    observer.next(backendResponse);
                    observer.complete();
                  },
                  error: (error) => {
                    observer.error(error);
                  }
                });
            },
            error: (error) => {
              console.error('Microsoft login failed:', error);
              observer.error(error);
            }
          });
      }).catch(error => {
        console.error('MSAL initialization failed:', error);
        observer.error(error);
      });
    });
  }

  /**
   * Exchanges Microsoft ID token with backend for JWT tokens
   */
  private exchangeTokenWithBackend(idToken: string): Observable<any> {
    return this.http.post(`${this.apiUrl}microsoft/callback`, {
      idToken: idToken
    });
  }

  /**
   * Check if email exists in Azure AD
   */
  checkEmailInAzureAd(email: string): Observable<{ existsInAzureAd: boolean }> {
    return this.http.post<{ existsInAzureAd: boolean }>(`${this.apiUrl}microsoft/check-user`, {
      email: email
    });
  }

  /**
   * Logout from Microsoft
   */
  logoutMicrosoft(): void {
    this.msalService.logoutPopup();
  }
}
